import { ApiError, query } from './client'
import type {
  ChallengeKind,
  ChallengePool,
  CreatedEvent,
  EventDashboard,
  EventGuests,
  EventPatch,
  EventPeople,
  EventPerson,
  EventPhotoUpload,
  GuestSort,
  MyEvent,
  MyInvite,
  ExportView,
  Exports,
  Owner,
  OwnerChallenge,
  OwnerChallenges,
  OwnerEvent,
} from './ownerTypes'

/**
 * De eigenaarskant praat met cookies en niet met een header-token.
 *
 * Vandaar `credentials: 'same-origin'` en een eigen `request`: de gastenkant
 * hangt een `X-Invite-Token` aan elk verzoek en heeft juist géén cookie nodig.
 * Die twee door elkaar halen zou betekenen dat een gast per ongeluk als
 * eigenaar kan doorgaan omdat er toevallig nog een sessie openstond.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {}

  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const response = await fetch(path, {
    ...init,
    headers: { ...headers, ...(init.headers as Record<string, string> | undefined) },
    credentials: 'same-origin',
  })

  if (!response.ok) {
    throw new ApiError(response.status, await describe(response))
  }

  // 204 heeft nooit een body, en 202 hier ook niet: "wachtwoord vergeten"
  // antwoordt met opzet leeg, want er valt niets terug te melden zonder te
  // verklappen of dat adres bestaat. Op de lengte kijken en niet alleen op de
  // status, zodat een endpoint dat later leeg antwoordt hier niet omvalt.
  const body = await response.text()

  if (body === '') {
    return undefined as T
  }

  return JSON.parse(body) as T
}

async function describe(response: Response): Promise<string> {
  try {
    const problem = (await response.json()) as {
      title?: string
      detail?: string
      errors?: Record<string, string[]>
    }

    const firstFieldError = problem.errors ? Object.values(problem.errors).flat().at(0) : undefined

    return firstFieldError ?? problem.detail ?? problem.title ?? `Fout ${response.status}`
  } catch {
    return `Fout ${response.status}`
  }
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

// --- Sessie ------------------------------------------------------------------

export function signIn(email: string, password: string): Promise<Owner> {
  return request<Owner>('/api/owner/sessions', { method: 'POST', ...json({ email, password }) })
}

export function signOut(): Promise<void> {
  return request<void>('/api/owner/sessions', { method: 'DELETE' })
}

export function getOwner(): Promise<Owner> {
  return request<Owner>('/api/owner/me')
}

// --- Events ------------------------------------------------------------------

export function listMyEvents(): Promise<{ events: MyEvent[] }> {
  return request<{ events: MyEvent[] }>('/api/owner/events')
}

export function createEvent(title: string, eventDateUtc: string | null): Promise<CreatedEvent> {
  return request<CreatedEvent>('/api/owner/events', {
    method: 'POST',
    ...json({ title, eventDateUtc }),
  })
}

export function getOwnerEvent(eventId: string): Promise<OwnerEvent> {
  return request<OwnerEvent>(`/api/owner/events/${eventId}`)
}

export function updateEvent(eventId: string, patch: EventPatch): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}`, { method: 'PATCH', ...json(patch) })
}

export function getEventDashboard(eventId: string): Promise<EventDashboard> {
  return request<EventDashboard>(`/api/owner/events/${eventId}/dashboard`)
}

export function listEventGuests(
  eventId: string,
  sort: GuestSort,
  skip: number,
  take: number,
): Promise<EventGuests> {
  return request<EventGuests>(`/api/owner/events/${eventId}/guests${query({ sort, skip, take })}`)
}

// --- Foto's op de landing ----------------------------------------------------

export function createEventPhotoUpload(
  eventId: string,
  body: {
    fileName: string
    contentType: string
    sizeBytes: number
    width?: number
    height?: number
  },
): Promise<EventPhotoUpload> {
  return request<EventPhotoUpload>(`/api/owner/events/${eventId}/photos/uploads`, {
    method: 'POST',
    ...json(body),
  })
}

export function completeEventPhoto(
  eventId: string,
  photoId: string,
  size: { width?: number; height?: number },
): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}/photos/${photoId}/complete`, {
    method: 'POST',
    ...json(size),
  })
}

export function reorderEventPhotos(eventId: string, photoIds: string[]): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}/photos/order`, {
    method: 'PUT',
    ...json({ photoIds }),
  })
}

export function deleteEventPhoto(eventId: string, photoId: string): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}/photos/${photoId}`, { method: 'DELETE' })
}

// --- Drukwerk ----------------------------------------------------------------

export function qrPngUrl(eventId: string): string {
  return `/api/owner/events/${eventId}/qr.png`
}

export function tableCardPdfUrl(eventId: string): string {
  return `/api/owner/events/${eventId}/tafelkaartje.pdf`
}

// --- Registreren en wachtwoord -----------------------------------------------

export function register(displayName: string, email: string, password: string): Promise<Owner> {
  return request<Owner>('/api/owner/accounts', {
    method: 'POST',
    ...json({ displayName, email, password }),
  })
}

/**
 * Antwoordt ook met 202 als het adres niet bestaat. Dat is met opzet: zou het
 * verschillen, dan is dit formulier een manier om af te lopen wie er een
 * account heeft.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return request<void>('/api/owner/password-resets', { method: 'POST', ...json({ email }) })
}

export function resetPassword(token: string, password: string): Promise<Owner> {
  return request<Owner>(`/api/owner/password-resets/${encodeURIComponent(token)}`, {
    method: 'POST',
    ...json({ password }),
  })
}

// --- Exporteren --------------------------------------------------------------

export function listExports(eventId: string): Promise<Exports> {
  return request<Exports>(`/api/owner/events/${eventId}/exports`)
}

export function startExport(eventId: string): Promise<ExportView> {
  return request<ExportView>(`/api/owner/events/${eventId}/exports`, { method: 'POST' })
}

export function getExport(eventId: string, exportId: string): Promise<ExportView> {
  return request<ExportView>(`/api/owner/events/${eventId}/exports/${exportId}`)
}

export function cancelExport(eventId: string, exportId: string): Promise<ExportView> {
  return request<ExportView>(`/api/owner/events/${eventId}/exports/${exportId}/cancel`, {
    method: 'POST',
  })
}

/**
 * Een gewone link en geen fetch: de server stuurt een redirect naar een
 * presigned URL, en die bytes horen niet door onze code te lopen.
 */
export function exportDownloadUrl(eventId: string, exportId: string): string {
  return `/api/owner/events/${eventId}/exports/${exportId}/download`
}

// --- Challenges --------------------------------------------------------------

export function listOwnerChallenges(eventId: string): Promise<OwnerChallenges> {
  return request<OwnerChallenges>(`/api/owner/events/${eventId}/challenges`)
}

export function createChallenge(
  eventId: string,
  body: { title: string; kind: ChallengeKind; unlocksAtUtc?: string | null },
): Promise<OwnerChallenge> {
  return request<OwnerChallenge>(`/api/owner/events/${eventId}/challenges`, {
    method: 'POST',
    ...json(body),
  })
}

export function addChallengesFromPool(
  eventId: string,
  suggestionIds: string[],
): Promise<OwnerChallenges> {
  return request<OwnerChallenges>(`/api/owner/events/${eventId}/challenges/from-pool`, {
    method: 'POST',
    ...json({ suggestionIds }),
  })
}

export function updateChallenge(
  eventId: string,
  challengeId: string,
  body: {
    title?: string
    kind?: ChallengeKind
    unlocksAtUtc?: string | null
    clearUnlocksAt?: boolean
    isVisible?: boolean
  },
): Promise<OwnerChallenge> {
  return request<OwnerChallenge>(`/api/owner/events/${eventId}/challenges/${challengeId}`, {
    method: 'PATCH',
    ...json(body),
  })
}

export function deleteChallenge(eventId: string, challengeId: string): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}/challenges/${challengeId}`, {
    method: 'DELETE',
  })
}

export function reorderChallenges(eventId: string, challengeIds: string[]): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}/challenges/order`, {
    method: 'PUT',
    ...json({ challengeIds }),
  })
}

export function getChallengePool(): Promise<ChallengePool> {
  return request<ChallengePool>('/api/owner/challenge-pool')
}

// --- Wie beheert er mee ------------------------------------------------------

export function listEventPeople(eventId: string): Promise<EventPeople> {
  return request<EventPeople>(`/api/owner/events/${eventId}/mensen`)
}

export function inviteMember(eventId: string, email: string): Promise<EventPerson> {
  return request<EventPerson>(`/api/owner/events/${eventId}/mensen`, {
    method: 'POST',
    ...json({ email }),
  })
}

export function removeMember(eventId: string, memberId: string): Promise<void> {
  return request<void>(`/api/owner/events/${eventId}/mensen/${memberId}`, { method: 'DELETE' })
}

export function listMyInvites(): Promise<{ invites: MyInvite[] }> {
  return request<{ invites: MyInvite[] }>('/api/owner/uitnodigingen')
}

export function respondToInvite(memberId: string, accept: boolean): Promise<void> {
  return request<void>(`/api/owner/uitnodigingen/${memberId}`, {
    method: 'POST',
    ...json({ accept }),
  })
}
